// src/lib/schema/components/LocalBusinessSchema.tsx

import React from 'react';
import { generateLocalBusinessSchema, generateMultipleStoresSchema, schemaToString } from '@/lib/schema';

/**
 * Interface for store address
 */
interface StoreAddress {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

/**
 * Interface for store coordinates
 */
interface StoreCoordinates {
  lat: number;
  lng: number;
}

/**
 * Interface for store opening hours
 */
interface StoreOpeningHours {
  days: string | string[];
  opens: string;
  closes: string;
}

/**
 * Interface for store special opening hours
 */
interface StoreSpecialOpeningHours extends StoreOpeningHours {
  validFrom: string;
  validThrough: string;
}

/**
 * Interface for store department
 */
interface StoreDepartment {
  name: string;
  description?: string;
}

/**
 * Interface for store information
 */
interface StoreInfo {
  id?: string;
  type?: string;
  name?: string;
  location?: string;
  phone?: string;
  email?: string;
  image?: string;
  address?: StoreAddress;
  coordinates?: StoreCoordinates;
  openingHours?: StoreOpeningHours[];
  specialOpeningHours?: StoreSpecialOpeningHours[];
  paymentMethods?: string;
  priceRange?: string;
  rating?: number;
  reviewCount?: number;
  departments?: StoreDepartment[];
  amenities?: string[];
  [key: string]: any;
}

/**
 * Props for the LocalBusinessSchema component
 */
interface LocalBusinessSchemaProps {
  storeId: string;
  storeInfo: StoreInfo;
}

/**
 * Component that renders the JSON-LD schema for a local business
 */
export function LocalBusinessSchema({ storeId, storeInfo }: LocalBusinessSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!storeId || !storeInfo) return null;

  // Generate the local business schema
  const businessSchema = generateLocalBusinessSchema(storeId, storeInfo);

  // Return null if schema generation failed
  if (!businessSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(businessSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}

/**
 * Props for the MultipleStoresSchema component
 */
interface MultipleStoresSchemaProps {
  stores: StoreInfo[];
  lang: string;
}

/**
 * Component that renders the JSON-LD schema for multiple stores
 */
export function MultipleStoresSchema({ stores, lang }: MultipleStoresSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!stores || !Array.isArray(stores) || stores.length === 0 || !lang) return null;

  // Generate the multiple stores schema
  const storesSchema = generateMultipleStoresSchema(stores, lang);

  // Return null if schema generation failed
  if (!storesSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(storesSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}